import { computed, onBeforeUnmount, onMounted, ref, toValue, unref, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { get } from 'lodash';

import { useEventHub } from '@satellite/composables/useEventHub';
import { yardQueryKeys } from './yard/yardQueryKeys';
import yardSpotService from '@satellite/services/yardSpot-service';

import type { ComputedRef, Ref } from 'vue';
import type { Maybe } from '@nova/core';

export interface YardSpot {
  id: string;
  code: string;
  type: string;
  spotAreaId: string;
}

export interface UseYardSpotPresenterV2Options {
  /**
   * Spot ID to fetch. Can be a ref for reactivity.
   * Optional if spot (with id) is provided.
   */
  spotId?: Ref<Maybe<string>> | ComputedRef<Maybe<string>>;

  /**
   * Optional spot object. Used as placeholderData while the query loads/refetches.
   * Effective id is derived from spotId or spot.id (explicit spotId wins; mismatch triggers a warning).
   */
  spot?: Ref<Maybe<YardSpot>> | ComputedRef<Maybe<YardSpot>> | YardSpot;

  /**
   * If true, subscribes to websocket events and invalidates query on changes.
   * Default: true
   */
  live?: boolean;

  /**
   * Stale time in milliseconds. Data older than this triggers background refetch.
   * Default: Infinity (never stale, until a Subspace event invalidates the query; manual refresh is always available)
   */
  staleTime?: number;

  /**
   * Callback for when spot is updated.
   */
  onUpdateReceived?: (spot: YardSpot) => void;
}

export function useYardSpotPresenterV2(options: UseYardSpotPresenterV2Options) {
  const { spotId, spot: spotOption, live = true, staleTime = Infinity, onUpdateReceived } = options;

  const eventHub = useEventHub();
  const queryClient = useQueryClient();

  const effectiveSpotId = computed((): string | null => {
    const explicitId = unref(spotId);
    const fromObject = toValue(spotOption)?.id ?? null;

    if (explicitId) {
      if (fromObject && fromObject !== explicitId) {
        console.warn(
          '[useYardSpotPresenterV2] spotId mismatch:',
          `explicit spotId (${explicitId}) does not match spot.id (${fromObject}). Using explicit spotId.`
        );
      }
      return explicitId;
    }
    return fromObject;
  });

  const spotPlaceholder = computed(() => toValue(spotOption) ?? undefined);

  const isInViewport = ref(true);
  const pendingInvalidation = ref(false);

  const handleIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
    const isIntersecting = typeof entry === 'boolean' ? entry : entry?.isIntersecting ?? true;
    isInViewport.value = isIntersecting;

    if (isIntersecting && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  const spotQuery = useQuery({
    queryKey: computed(() =>
      effectiveSpotId.value ? yardQueryKeys.spotDetail(effectiveSpotId.value) : []
    ),
    queryFn: async () => {
      const response = await yardSpotService.getSpotDetails(effectiveSpotId.value!, {
        requestConfig: {
          params: {
            // No joins - just the spot itself
            // Assignments will be fetched separately via getSpotAssignments()
          }
        },
        enableCache: true
      });

      const spot: YardSpot = {
        id: get(response, 'id') ?? '',
        code: get(response, 'code') ?? '',
        type: get(response, 'type') ?? '',
        spotAreaId: get(response, 'spotAreaId') ?? ''
      };

      return spot;
    },
    enabled: computed(() => Boolean(effectiveSpotId.value) && isInViewport.value),
    placeholderData: spotPlaceholder,
    staleTime,
    refetchOnWindowFocus: false
  });

  const spot = computed(() => spotQuery.data.value || null);
  const isLoading = computed(() => spotQuery.isLoading.value);
  const isFetching = computed(() => spotQuery.isFetching.value);
  const error = computed(() => spotQuery.error.value);

  const spotCode = computed(() => spot.value?.code || null);
  const areaId = computed(() => spot.value?.spotAreaId || null);

  const invalidate = () => {
    if (effectiveSpotId.value) {
      queryClient.invalidateQueries({
        queryKey: yardQueryKeys.spot(effectiveSpotId.value)
      });
    }
  };

  const refresh = async () => {
    await spotQuery.refetch();
  };

  const handleSpotEvent = (event: { id?: string }) => {
    if (event.id === effectiveSpotId.value) {
      if (isInViewport.value) {
        invalidate();
      } else {
        pendingInvalidation.value = true;
      }
    }
  };

  watch(spot, newSpot => {
    if (onUpdateReceived && newSpot) {
      onUpdateReceived(newSpot);
    }
  });

  onMounted(() => {
    if (live) {
      eventHub.on('update-Spot', handleSpotEvent);
      eventHub.on('delete-Spot', handleSpotEvent);
    }
  });

  onBeforeUnmount(() => {
    if (live) {
      eventHub.off('update-Spot', handleSpotEvent);
      eventHub.off('delete-Spot', handleSpotEvent);
    }
  });

  return {
    spot,
    spotCode,
    areaId,

    isLoading,
    isFetching,
    error,

    refresh,
    invalidate,

    onIntersect: handleIntersect,

    query: spotQuery
  };
}
